import * as yup from "yup";
import { commonConstants, serviceRequestConstants } from "../_redux/_constants";
import { isServiceRequestFileUploadValueEmpty } from "./serviceRequestFileUpload.helper";
import NumberFormatter from "./numberFormatter";
import { DateFormatter } from "./dateFormatter";
import {
  formatServiceRequestSensitiveDisplayValue,
  isServiceRequestSensitiveField,
} from "./serviceRequest.helper";

export function hasServiceRequestWizardProgress(initiateData) {
  if (initiateData == null) {
    return false;
  }

  if (Array.isArray(initiateData)) {
    return initiateData.length > 0;
  }

  if (typeof initiateData === "object") {
    return Object.keys(initiateData).length > 0;
  }

  return false;
}

export function getSafeServiceRequestValidationRegex(validationRegex) {
  if (!validationRegex || typeof validationRegex !== "string") {
    return null;
  }
  try {
    return new RegExp(validationRegex);
  } catch (err) {
    console.warn("Invalid regex for service request field:", err);
    return null;
  }
}

export function appendTextInputReconfirmationToSchemaShape(
  shape,
  field,
  { isRequired, label, mismatchMessage = "The entered values do not match" } = {},
) {
  const code = field?.fieldCode;
  if (!code) return shape;

  let mainSchema = yup.string();

  if (field?.isNumeric === commonConstants.YES_OPTION_TYPE) {
    mainSchema = mainSchema.matches(
      /^\d+$/,
      `${label} must contain only digits`,
    );
  }

  const regex = getSafeServiceRequestValidationRegex(field?.validationRegex);
  if (regex) {
    mainSchema = mainSchema.matches(regex, `${label} format is invalid`);
  }

  if (isRequired) {
    mainSchema = mainSchema.required(`${label} is required`);
  }

  shape[code] = mainSchema;

  if (field?.isReconfirmationField !== commonConstants.YES_OPTION_TYPE) {
    return shape;
  }

  let confirmSchema = yup.string();
  if (isRequired) {
    confirmSchema = confirmSchema.required(`${label} confirmation is required`);
  }
  confirmSchema = confirmSchema.test(
    "must-match",
    mismatchMessage,
    function (val) {
      const mainVal = this.parent[code];
      return !val || !mainVal || val === mainVal;
    },
  );
  shape[`${code}_confirm`] = confirmSchema;
  return shape;
}

export function getServiceRequestNumberSuffix(numberType) {
  if (numberType === serviceRequestConstants.NUMBER_TYPE_PERCENTAGE) {
    return "%";
  }
  return null;
}

export function formatServiceRequestNumberDisplayValue(
  fieldMeta,
  rawValue,
  fallback = "-",
) {
  if (rawValue == null || rawValue === "") {
    return fallback;
  }

  if (isServiceRequestSensitiveField(fieldMeta)) {
    return formatServiceRequestSensitiveDisplayValue(
      rawValue,
      fieldMeta,
      fallback,
    );
  }

  const suffix = getServiceRequestNumberSuffix(fieldMeta?.numberType);
  const normalized = String(rawValue).trim();
  if (!normalized) {
    return fallback;
  }

  return suffix ? `${normalized}${suffix}` : normalized;
}

export function appendServiceRequestNumberFieldToSchemaShape(
  shape,
  field,
  { isRequired, label, t } = {},
) {
  const code = field?.fieldCode;
  if (!code) {
    return shape;
  }

  let schema = yup
    .number()
    .transform((value, originalValue) =>
      originalValue === "" || originalValue === null || originalValue === undefined
        ? undefined
        : value,
    )
    .typeError(t("{{label}} must contain only digits", { label }));

  if (isRequired) {
    schema = schema.required(t("{{label}} is required", { label }));
  }

  if (field?.numberType === serviceRequestConstants.NUMBER_TYPE_PERCENTAGE) {
    schema = schema.moreThan(0, t("Value must be greater than 0%"));
    schema = schema.max(100, t("Value cannot exceed 100%"));
  } else {
    const minLimit = parseFloat(field?.minimumLimit);
    const maxLimit = parseFloat(field?.maximumLimit);

    if (!isNaN(minLimit)) {
      schema = schema.min(
        minLimit,
        t("{{label}} must be at least {{minLimit}}", { label, minLimit }),
      );
    }

    if (!isNaN(maxLimit)) {
      schema = schema.max(
        maxLimit,
        t("{{label}} must be at most {{maxLimit}}", { label, maxLimit }),
      );
    }
  }

  shape[code] = schema;
  return shape;
}

const ASSET_STORE_KEY_SEPARATOR = "::";

export function getAssetOptionsStoreKey(assetType, fieldCode) {
  if (!assetType) return "";
  if (
    !fieldCode ||
    assetType === serviceRequestConstants.CUSTOMER_ADDRESS_ASSET
  ) {
    return assetType;
  }
  return `${assetType}${ASSET_STORE_KEY_SEPARATOR}${fieldCode}`;
}

export function normalizeServiceRequestFilterFieldParent(raw) {
  if (raw == null) return null;
  if (Array.isArray(raw)) {
    if (raw.length === 0) return null;
    const first = raw[0];
    return first && typeof first === "object" && first.fieldCode ? first : null;
  }
  if (typeof raw === "object" && raw.fieldCode) return raw;
  return null;
}

export function normalizeServiceRequestFilterScalarFieldValue(
  parentField,
  rawValue,
) {
  if (rawValue == null || rawValue === "") {
    return rawValue;
  }

  const fieldType = parentField?.fieldType;
  const shouldNormalize =
    fieldType === serviceRequestConstants.AMOUNT_FIELD_TYPE ||
    fieldType === serviceRequestConstants.NUMBER_FIELD_TYPE ||
    fieldType === serviceRequestConstants.FORMULA_FIELD_TYPE ||
    (fieldType === serviceRequestConstants.TEXT_INPUT_FIELD_TYPE &&
      parentField?.isNumeric === commonConstants.YES_OPTION_TYPE);

  if (!shouldNormalize) {
    return rawValue;
  }

  const withoutCurrency = String(rawValue)
    .trim()
    .replace(/^[A-Za-z]{3}\s+/, "");
  const normalized = withoutCurrency.replace(/,/g, "");

  if (/^-?\d+(\.\d+)?$/.test(normalized)) {
    return normalized;
  }

  return rawValue;
}

export function shouldDebounceServiceRequestAssetFetch(
  fieldData,
  serviceRequestTemplateFieldsList = [],
) {
  const parentRefs = Array.isArray(fieldData?.filterFieldParent)
    ? fieldData.filterFieldParent
    : [];

  if (!parentRefs.length) {
    return false;
  }

  return parentRefs.some((parentRef) => {
    const parentField = serviceRequestTemplateFieldsList?.find(
      (templateField) => templateField?.fieldCode === parentRef?.fieldCode,
    );

    if (!parentField) {
      return false;
    }

    if (
      parentField.fieldType === serviceRequestConstants.AMOUNT_FIELD_TYPE ||
      parentField.fieldType === serviceRequestConstants.NUMBER_FIELD_TYPE ||
      parentField.fieldType === serviceRequestConstants.FORMULA_FIELD_TYPE
    ) {
      return true;
    }

    return (
      parentField.fieldType === serviceRequestConstants.TEXT_INPUT_FIELD_TYPE &&
      parentField.isNumeric === commonConstants.YES_OPTION_TYPE
    );
  });
}

function normalizeServiceRequestFilterDateValue(value) {
  if (value == null || value === "") return null;
  if (typeof value === "string") {
    const parsed = parseIsoDateOnly(value);
    if (!parsed) return value;
    const y = parsed.getFullYear();
    const m = String(parsed.getMonth() + 1).padStart(2, "0");
    const d = String(parsed.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }
  if (typeof value === "object" && typeof value.format === "function") {
    const formatted = value.format("YYYY-MM-DD");
    return formatted || null;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function resolveAssetFilterIdFromFilterFields(
  parentRefs,
  requestFilterFields,
  serviceRequestTemplateFieldsList,
) {
  for (const parentRef of parentRefs) {
    const parentField = serviceRequestTemplateFieldsList?.find(
      (templateField) => templateField?.fieldCode === parentRef?.fieldCode,
    );
    if (
      parentField?.fieldType !== serviceRequestConstants.ASSET_TYPE_FIELD_TYPE
    ) {
      continue;
    }
    const mapKey = parentRef?.filterFieldKey || parentRef?.fieldCode;
    const entry = mapKey ? requestFilterFields?.[mapKey] : null;
    if (entry?.fieldValue != null && String(entry.fieldValue).trim() !== "") {
      return String(entry.fieldValue);
    }
  }
  return undefined;
}

export function buildFilterFieldValueForAssetRequest(
  parentField,
  values,
  serviceRequestAssetOptions = {},
  filterFieldKey = "",
) {
  if (!parentField?.fieldCode) return null;
  const code = parentField.fieldCode;
  const fieldType = parentField?.fieldType;
  const withFilterFieldCode = (result) => {
    if (!filterFieldKey || result == null) return result;
    return { fieldCode: filterFieldKey, ...result };
  };

  if (fieldType === serviceRequestConstants.DATE_RANGE_FIELD_TYPE) {
    const from = normalizeServiceRequestFilterDateValue(
      values?.[`${code}_from`],
    );
    const to = normalizeServiceRequestFilterDateValue(values?.[`${code}_to`]);
    if (!from || !to) return null;

    const fieldValue = `${from} ${to}`;
    return withFilterFieldCode({
      fieldValue,
      fromValue: from,
      toValue: to,
      heading: fieldValue,
    });
  }

  if (fieldType === serviceRequestConstants.VALUE_RANGE_FIELD_TYPE) {
    const startValue = values?.[`${code}_from`];
    const endValue = values?.[`${code}_to`];
    const rangeEmpty =
      startValue == null ||
      startValue === "" ||
      endValue == null ||
      endValue === "";
    if (rangeEmpty) {
      return null;
    }

    return withFilterFieldCode({
      fieldValue: `${startValue}-${endValue}`,
      startValue,
      endValue,
    });
  }

  const rawValue = values?.[code];
  const isEmpty =
    rawValue == null ||
    rawValue === "" ||
    (Array.isArray(rawValue) && rawValue.length === 0);
  if (isEmpty) return null;

  if (
    fieldType === serviceRequestConstants.TEXT_INPUT_FIELD_TYPE ||
    fieldType === serviceRequestConstants.AMOUNT_FIELD_TYPE ||
    fieldType === serviceRequestConstants.NUMBER_FIELD_TYPE ||
    fieldType === serviceRequestConstants.DATE_FIELD_TYPE ||
    fieldType === serviceRequestConstants.FORMULA_FIELD_TYPE
  ) {
    const scalarValue =
      fieldType === serviceRequestConstants.DATE_FIELD_TYPE
        ? normalizeServiceRequestFilterDateValue(rawValue)
        : normalizeServiceRequestFilterScalarFieldValue(parentField, rawValue);
    if (scalarValue == null || scalarValue === "") return null;

    const result = { fieldValue: scalarValue };
    if (
      fieldType === serviceRequestConstants.TEXT_INPUT_FIELD_TYPE &&
      parentField?.isReconfirmationField === commonConstants.YES_OPTION_TYPE
    ) {
      result.confirmFieldValue = values?.[`${code}_confirm`] ?? null;
    }
    return withFilterFieldCode(result);
  }

  if (fieldType === serviceRequestConstants.DROPDOWN_FIELD_TYPE) {
    const selectedOption = (parentField?.customDropdownOptions || []).find(
      (opt) => String(opt?.optionCode) === String(rawValue),
    );
    return withFilterFieldCode({
      fieldValue: rawValue,
      ...(selectedOption?.optionValue
        ? { dropdownValue: selectedOption.optionValue }
        : {}),
      ...(selectedOption?.customOptionCode
        ? { dropdownCode: selectedOption.customOptionCode }
        : selectedOption?.optionCode != null
          ? { dropdownCode: selectedOption.optionCode }
          : {}),
    });
  }

  if (fieldType === serviceRequestConstants.FILE_UPLOAD_FIELD_TYPE) {
    const fileUploadId =
      rawValue && typeof rawValue === "object" && rawValue.fileUploadId != null
        ? String(rawValue.fileUploadId)
        : rawValue;
    return fileUploadId
      ? withFilterFieldCode({ fieldValue: fileUploadId })
      : null;
  }

  if (fieldType === serviceRequestConstants.ASSET_TYPE_FIELD_TYPE) {
    if (typeof rawValue === "object" && rawValue !== null) {
      const fieldValue = rawValue?.fieldValue ?? rawValue?.id ?? rawValue?.value;
      if (fieldValue == null || fieldValue === "") return null;
      const heading = rawValue?.heading ?? rawValue?.dropdownValue;
      return withFilterFieldCode({
        fieldValue,
        ...(heading ? { heading, dropdownValue: heading } : {}),
        dropdownCode: String(fieldValue),
        ...(rawValue?.subHeading1 ? { subHeading1: rawValue.subHeading1 } : {}),
        ...(rawValue?.subHeading2 ? { subHeading2: rawValue.subHeading2 } : {}),
      });
    }
    const assetTypeName = parentField?.assetsType?.name;
    const storeKey = getAssetOptionsStoreKey(
      assetTypeName,
      parentField?.fieldCode,
    );
    const assetData = serviceRequestAssetOptions?.[storeKey];
    const rawValueStr = String(rawValue);
    const matchesAssetRow = (item) =>
      item != null &&
      (String(item?.id) === rawValueStr ||
        String(item?.value) === rawValueStr ||
        String(item?.heading) === rawValueStr);
    const selectedItem = Array.isArray(assetData)
      ? assetData.find(matchesAssetRow)
      : assetData && typeof assetData === "object"
        ? matchesAssetRow(assetData)
          ? assetData
          : null
        : null;
    const fieldValue = selectedItem?.id ?? rawValue;
    const heading = selectedItem?.heading;
    return withFilterFieldCode({
      fieldValue,
      ...(heading ? { heading, dropdownValue: heading } : {}),
      dropdownCode: String(fieldValue),
      ...(selectedItem?.subHeading1
        ? { subHeading1: selectedItem.subHeading1 }
        : {}),
      ...(selectedItem?.subHeading2
        ? { subHeading2: selectedItem.subHeading2 }
        : {}),
    });
  }

  return withFilterFieldCode({ fieldValue: rawValue });
}

export function isServiceRequestFilterDependentAssetField(
  field,
  serviceRequestTemplateFieldsList = [],
) {
  if (field?.isApplyFilter !== commonConstants.YES_OPTION_TYPE) {
    return false;
  }

  if (normalizeServiceRequestFilterFieldParent(field?.filterFieldParent)) {
    return true;
  }

  return !!serviceRequestTemplateFieldsList?.find(
    (f) =>
      Array.isArray(f?.filterFieldChilds) &&
      f.filterFieldChilds.some((child) => child.fieldCode === field?.fieldCode),
  );
}

export function buildAssetFilterRequest(
  assetField,
  values,
  serviceRequestTemplateFieldsList,
  serviceRequestAssetOptions = {},
) {
  const parentRefs = Array.isArray(assetField?.filterFieldParent)
    ? assetField.filterFieldParent
    : [];
  const shouldUseFilterFieldsMap =
    parentRefs.length > 1 ||
    parentRefs.some((parent) => !!parent?.filterFieldKey);

  if (shouldUseFilterFieldsMap) {
    const buildKeyedMappedFields = (assetOptions) => {
      const mappedFilterFields = {};
      const allRequiredParentsSatisfied = parentRefs.every((parentRef) => {
        const parentField = serviceRequestTemplateFieldsList?.find(
          (templateField) => templateField?.fieldCode === parentRef?.fieldCode,
        );

        if (
          parentField &&
          !isServiceRequestFieldVisible(
            parentField,
            values,
            serviceRequestTemplateFieldsList,
          )
        ) {
          return true;
        }

        const mapKey = parentRef?.filterFieldKey || parentRef?.fieldCode;
        const normalized = buildFilterFieldValueForAssetRequest(
          parentField,
          values,
          assetOptions,
          parentRef?.filterFieldKey || "",
        );
        if (normalized && mapKey) {
          mappedFilterFields[mapKey] = normalized;
        }
        if (parentField?.isRequired !== commonConstants.YES_OPTION_TYPE) {
          return true;
        }
        return normalized != null;
      });

      return { mappedFilterFields, allRequiredParentsSatisfied };
    };

    const { mappedFilterFields: stableFilterFields, allRequiredParentsSatisfied } =
      buildKeyedMappedFields({});
    const { mappedFilterFields: requestFilterFields } = buildKeyedMappedFields(
      serviceRequestAssetOptions,
    );

    const hasMappedFilters = Object.keys(stableFilterFields).length > 0;
    const canFetch = hasMappedFilters && allRequiredParentsSatisfied;
    const filterId = canFetch
      ? resolveAssetFilterIdFromFilterFields(
          parentRefs,
          requestFilterFields,
          serviceRequestTemplateFieldsList,
        )
      : undefined;

    return {
      hasKeyedFilterParents: true,
      canFetch,
      filterRef: canFetch
        ? {
            filterFields: requestFilterFields,
            ...(filterId ? { filterId } : {}),
          }
        : "",
      dependencyKey: JSON.stringify(stableFilterFields),
    };
  }

  const normalizedParent = normalizeServiceRequestFilterFieldParent(
    assetField?.filterFieldParent,
  );
  const parentCode =
    normalizedParent?.fieldCode && values?.[normalizedParent.fieldCode];

  return {
    hasKeyedFilterParents: false,
    canFetch: !normalizedParent || !!parentCode,
    filterRef: parentCode || "",
    dependencyKey: String(parentCode ?? ""),
  };
}

export function isServiceRequestFieldVisible(
  field,
  values,
  serviceRequestTemplateFieldsList = [],
) {
  const parentField = serviceRequestTemplateFieldsList?.find((f) =>
    f?.conditionFieldChilds?.some(
      (child) => child.fieldCode === field?.fieldCode,
    ),
  );

  const isExplicitConditionalChild =
    field?.isShowCondition === commonConstants.YES_OPTION_TYPE;

  if (!isExplicitConditionalChild && !parentField) {
    return true;
  }

  if (!parentField) {
    return false;
  }

  if (
    !isServiceRequestFieldVisible(
      parentField,
      values,
      serviceRequestTemplateFieldsList,
    )
  ) {
    return false;
  }

  const childCondition = parentField.conditionFieldChilds.find(
    (child) => child.fieldCode === field?.fieldCode,
  );

  return parentValueMatchesShowCondition(
    parentField,
    values,
    childCondition?.showConditionValue,
  );
}

export function getServiceRequestSingleAssetFormValue(singleItem) {
  if (!singleItem || typeof singleItem !== "object") {
    return "";
  }

  const id = singleItem.id ?? singleItem.value;
  if (id != null && String(id).trim() !== "") {
    return String(id);
  }

  const heading = singleItem.heading;
  if (heading != null && String(heading).trim() !== "") {
    return String(heading);
  }

  return "";
}

export function parentValueMatchesShowCondition(
  parentField,
  values,
  showConditionValue,
) {
  if (showConditionValue == null || showConditionValue === "") {
    return true;
  }

  const raw = values?.[parentField?.fieldCode];
  if (Array.isArray(raw)) {
    return raw.some((entry) => {
      const value =
        typeof entry === "object" && entry != null
          ? (entry.optionCode ?? entry.fieldValue ?? entry.id ?? entry)
          : entry;
      return String(value) === String(showConditionValue);
    });
  }

  return String(raw) === String(showConditionValue);
}

export function buildServiceRequestTermsPayload(
  termsAndConditionRequired,
  termsAndConditionUrl,
) {
  if (termsAndConditionRequired !== commonConstants.YES_OPTION_TYPE) {
    return {};
  }

  if (!termsAndConditionUrl) {
    return {};
  }

  return { termsAndConditionUrl };
}

export function getServiceRequestAssetHeading(item) {
  const raw = item?.heading ?? item?.name;
  if (raw == null || raw === "") return "";
  return String(raw);
}

export function normalizeServiceRequestAssetListRow(item) {
  const id = item?.id ?? item?.value ?? "";
  return {
    id,
    value: item?.value ?? id,
    heading: getServiceRequestAssetHeading(item),
    subHeading1: item?.subHeading1 ?? "",
    subHeading2: item?.subHeading2 ?? "",
    subHeading3: item?.subHeading3 ?? "",
  };
}

export function mapRawToAssetRow(option) {
  return normalizeServiceRequestAssetListRow({
    ...option,
    id: option?.id ?? option?.value,
  });
}

function findAssetRowMatch(optionRows, rawEntry) {
  if (rawEntry == null || rawEntry === "") return null;
  const idStr =
    typeof rawEntry === "object"
      ? String(rawEntry.id ?? rawEntry.value ?? rawEntry.fieldValue ?? "")
      : String(rawEntry);
  if (!idStr) return null;

  return (
    optionRows.find(
      (row) =>
        String(row?.id) === idStr ||
        String(row?.value) === idStr ||
        String(row?.heading) === idStr ||
        idStr.includes(String(row?.id ?? "")),
    ) ?? null
  );
}

export function normalizeAssetMultiSelection(raw, optionRows = []) {
  if (Array.isArray(raw) && raw.length > 0 && typeof raw[0] === "object") {
    return raw.map((row) => normalizeServiceRequestAssetListRow(row));
  }

  const ids = Array.isArray(raw) ? raw : raw != null && raw !== "" ? [raw] : [];

  return ids.map((entry) => {
    const match = findAssetRowMatch(optionRows, entry);
    if (match) {
      return normalizeServiceRequestAssetListRow({
        ...match,
        id: match.id ?? match.value ?? entry,
      });
    }
    const idStr =
      typeof entry === "object"
        ? String(entry?.id ?? entry?.value ?? entry?.fieldValue ?? "")
        : String(entry ?? "");
    return normalizeServiceRequestAssetListRow({
      id: idStr,
      heading: "",
    });
  });
}

export function getServiceRequestSingleAssetSelectedItem(
  items,
  selectedValue,
  { defaultToFirstWhenUnmatched = true } = {},
) {
  const rows = Array.isArray(items)
    ? items
    : items != null && typeof items === "object"
      ? [items]
      : [];

  if (selectedValue != null && selectedValue !== "") {
    const matched = rows.find(
      (it) =>
        String(it?.id) === String(selectedValue) ||
        String(it?.value) === String(selectedValue) ||
        String(it?.heading) === String(selectedValue),
    );
    if (matched) return matched;
  }

  if (rows.length === 1 && defaultToFirstWhenUnmatched) {
    return rows[0];
  }

  if (items != null && typeof items === "object" && !Array.isArray(items)) {
    return items;
  }

  return null;
}

export function serviceRequestSingleAssetValueMatchesItem(
  rawValue,
  singleItem,
) {
  if (!singleItem || rawValue == null || rawValue === "") {
    return false;
  }
  const value = String(rawValue);
  return (
    String(singleItem.id) === value ||
    String(singleItem.value) === value ||
    String(singleItem.heading) === value
  );
}

export function getServiceRequestSingleEditableAssetDisplayValue(
  rawValue,
  singleItem,
) {
  if (serviceRequestSingleAssetValueMatchesItem(rawValue, singleItem)) {
    return getServiceRequestAssetHeading(singleItem);
  }
  if (rawValue != null && String(rawValue).trim() !== "") {
    return String(rawValue);
  }
  return getServiceRequestAssetHeading(singleItem);
}

export function isSingleTemplateOneAssetField(fieldData) {
  return (
    fieldData?.fieldType === serviceRequestConstants.ASSET_TYPE_FIELD_TYPE &&
    fieldData?.assetsType?.displayType ===
      serviceRequestConstants.SERVICE_REQUEST_ASSET_DISPLAY_TYPE_SINGLE &&
    fieldData?.assetsType?.template ===
      serviceRequestConstants.ASSET_TEMPLATE_1_TYPE
  );
}

export function toServiceRequestAssetListRows(items, displayType) {
  if (
    displayType !==
    serviceRequestConstants.SERVICE_REQUEST_ASSET_DISPLAY_TYPE_LIST
  ) {
    return [];
  }
  if (Array.isArray(items)) return items;
  if (items != null && typeof items === "object") return [items];
  return [];
}

export function getServiceRequestAssetOptionRows(items, displayType) {
  if (
    displayType ===
    serviceRequestConstants.SERVICE_REQUEST_ASSET_DISPLAY_TYPE_LIST
  ) {
    return toServiceRequestAssetListRows(items, displayType);
  }
  if (Array.isArray(items)) return items;
  if (items != null && typeof items === "object") return [items];
  return [];
}

export function isOnlyOneServiceRequestAssetOption(items, displayType) {
  return getServiceRequestAssetOptionRows(items, displayType).length === 1;
}

export function shouldLockServiceRequestAssetSelection(
  isOnlyOneOption,
  hadMultipleOptions,
) {
  return Boolean(isOnlyOneOption && !hadMultipleOptions);
}

export function getServiceRequestMultiSelectAssetDisplayLimit(assetTemplate) {
  return assetTemplate === serviceRequestConstants.ASSET_TEMPLATE_1_TYPE
    ? serviceRequestConstants.SERVICE_REQUEST_MULTI_SELECT_ASSET_DISPLAY_LIMIT
    : 1;
}

const SERVICE_REQUEST_STRUCTURED_LIST_ASSET_TEMPLATES = new Set([
  serviceRequestConstants.ASSET_TEMPLATE_2_TYPE,
  serviceRequestConstants.ASSET_TEMPLATE_3_TYPE,
]);

export function isServiceRequestStructuredListAssetTemplate(assetTemplate) {
  return SERVICE_REQUEST_STRUCTURED_LIST_ASSET_TEMPLATES.has(assetTemplate);
}

export function isServiceRequestSingleStructuredAssetField(fieldMeta) {
  return (
    fieldMeta?.fieldType === serviceRequestConstants.ASSET_TYPE_FIELD_TYPE &&
    fieldMeta?.assetsType?.displayType ===
      serviceRequestConstants.SERVICE_REQUEST_ASSET_DISPLAY_TYPE_SINGLE &&
    isServiceRequestStructuredListAssetTemplate(fieldMeta?.assetsType?.template)
  );
}

export function resolveServiceRequestStructuredAssetConfirmRow(item) {
  if (item == null) {
    return null;
  }

  const raw =
    Array.isArray(item) &&
    item.length > 0 &&
    typeof item[0] === "object" &&
    item[0] != null
      ? item[0]
      : item;

  return raw != null && typeof raw === "object" ? raw : null;
}

function isEmptyFormValue(value) {
  return (
    value == null ||
    value === "" ||
    (Array.isArray(value) && value.length === 0)
  );
}

export function shouldOmitOptionalServiceRequestFieldFromSubmit(field, values) {
  if (field?.isRequired === commonConstants.YES_OPTION_TYPE) {
    return false;
  }

  const code = field?.fieldCode;
  const fieldType = field?.fieldType;

  if (
    fieldType === serviceRequestConstants.DROPDOWN_FIELD_TYPE &&
    field?.isMultiselect === commonConstants.YES_OPTION_TYPE
  ) {
    return isEmptyFormValue(values?.[code]);
  }

  if (
    fieldType === serviceRequestConstants.ASSET_TYPE_FIELD_TYPE &&
    field?.isMultiselect === commonConstants.YES_OPTION_TYPE
  ) {
    return isEmptyFormValue(values?.[code]);
  }

  if (fieldType === serviceRequestConstants.FILE_UPLOAD_FIELD_TYPE) {
    return isServiceRequestFileUploadValueEmpty(values?.[code]);
  }

  if (fieldType === serviceRequestConstants.DATE_FIELD_TYPE) {
    return !isServiceRequestDateFilled(values?.[code]);
  }

  if (fieldType === serviceRequestConstants.DATE_RANGE_FIELD_TYPE) {
    const fromFilled = isServiceRequestDateFilled(values?.[`${code}_from`]);
    const toFilled = isServiceRequestDateFilled(values?.[`${code}_to`]);
    return !fromFilled || !toFilled;
  }

  if (fieldType === serviceRequestConstants.VALUE_RANGE_FIELD_TYPE) {
    return (
      isEmptyFormValue(values?.[`${code}_from`]) ||
      isEmptyFormValue(values?.[`${code}_to`])
    );
  }

  return isEmptyFormValue(values?.[code]);
}

/**
 * @param {unknown} value
 * @returns {Date | null}
 */
export const parseIsoDateOnly = (value) => {
  if (!value || typeof value !== "string") return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Expand DD/MM/YY year using a sliding century pivot against the current year. */
const expandSlashDateYear = (yy) => {
  if (yy.length === 4) return yy;

  const twoDigitYear = parseInt(yy, 10);
  if (Number.isNaN(twoDigitYear)) return yy;

  const currentTwoDigitYear = new Date().getFullYear() % 100;
  const century = twoDigitYear > currentTwoDigitYear ? 1900 : 2000;
  return String(century + twoDigitYear);
};

/** Normalize restored DATE field values to ISO `YYYY-MM-DD` for form state. */
export function normalizeServiceRequestFormDateValue(value) {
  if (value == null || value === "") return undefined;

  if (typeof value === "object" && typeof value.format === "function") {
    const formatted = value.format("YYYY-MM-DD");
    return formatted || undefined;
  }

  if (typeof value !== "string") {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return undefined;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  const trimmed = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }

  const slashMatch = trimmed.match(/^(\d{2})\/(\d{2})\/(\d{2,4})$/);
  if (slashMatch) {
    const [, dd, mm, yy] = slashMatch;
    const year = expandSlashDateYear(yy);
    return `${year}-${mm}-${dd}`;
  }

  const parsed = parseIsoDateOnly(trimmed);
  if (!parsed) return undefined;

  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, "0");
  const d = String(parsed.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** Whether a DATE / DATE_RANGE side has a user-entered value (optional fields omit when false). */
export const isServiceRequestDateFilled = (value) => {
  if (value == null || value === "") return false;
  if (typeof value === "object" && typeof value.isValid === "function") {
    return value.isValid();
  }
  if (typeof value === "string") {
    return parseIsoDateOnly(value) != null;
  }
  const time = new Date(value).getTime();
  return !Number.isNaN(time);
};

/** DATE_RANGE: To must be strictly after From (same day fails). */
export function isServiceRequestToDateAfterFromDate(fromDate, toDate) {
  if (
    !isServiceRequestDateFilled(fromDate) ||
    !isServiceRequestDateFilled(toDate)
  ) {
    return true;
  }
  const fromIso = normalizeServiceRequestFormDateValue(fromDate);
  const toIso = normalizeServiceRequestFormDateValue(toDate);
  if (!fromIso || !toIso) {
    return false;
  }
  return toIso > fromIso;
}

/** DATE_RANGE: From is required when To has a value. */
export function isServiceRequestDateRangeFromRequired(fromDate, toDate) {
  if (!isServiceRequestDateFilled(toDate)) {
    return true;
  }
  return isServiceRequestDateFilled(fromDate);
}

/** DATE_RANGE: To is required when From has a value. */
export function isServiceRequestDateRangeToRequired(fromDate, toDate) {
  if (!isServiceRequestDateFilled(fromDate)) {
    return true;
  }
  return isServiceRequestDateFilled(toDate);
}

/**
 * @param {unknown} value ISO `YYYY-MM-DD`
 * @param {number} days
 * @returns {string | undefined} ISO `YYYY-MM-DD`
 */
export const addDaysToIsoDate = (value, days) => {
  const base = parseIsoDateOnly(value);
  if (!base || !Number.isFinite(days)) return undefined;
  const date = new Date(base);
  date.setDate(date.getDate() + days);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export const getTodayIsoDate = () => {
  const today = new Date();
  const y = today.getFullYear();
  const m = String(today.getMonth() + 1).padStart(2, "0");
  const d = String(today.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export const minIsoDate = (left, right) => {
  if (!left) return right;
  if (!right) return left;
  return left <= right ? left : right;
};

export const maxIsoDate = (left, right) => {
  if (!left) return right;
  if (!right) return left;
  return left >= right ? left : right;
};

const normalizeServiceRequestDateToIso = (value) => {
  if (value == null || value === "") return null;

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, "0");
    const d = String(value.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  if (typeof value === "object" && typeof value.format === "function") {
    const formatted = value.format("YYYY-MM-DD");
    return formatted || null;
  }

  const parsed = parseIsoDateOnly(String(value));
  if (!parsed) return null;

  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, "0");
  const d = String(parsed.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export const hasServiceRequestDateBoundsConfigError = (field) =>
  field?.enablePastDateOnly === commonConstants.YES_OPTION_TYPE &&
  field?.isFutureDate === commonConstants.YES_OPTION_TYPE;

export const getServiceRequestDateBounds = (field) => {
  if (hasServiceRequestDateBoundsConfigError(field)) {
    return {
      minDate: undefined,
      maxDate: undefined,
      hasConfigError: true,
    };
  }

  const excludeToday =
    field?.excludeCurrentDate === commonConstants.YES_OPTION_TYPE;
  const pastOnly =
    field?.enablePastDateOnly === commonConstants.YES_OPTION_TYPE;
  const futureOnly =
    field?.isFutureDate === commonConstants.YES_OPTION_TYPE;
  const today = getTodayIsoDate();

  let minDate;
  let maxDate;

  if (futureOnly) {
    minDate = excludeToday ? addDaysToIsoDate(today, 1) : today;
  }

  if (pastOnly) {
    maxDate = excludeToday ? addDaysToIsoDate(today, -1) : today;
  }

  return { minDate, maxDate, hasConfigError: false };
};

export const isIsoDateWithinBounds = (value, { minDate, maxDate } = {}) => {
  if (!value) return true;

  const iso = normalizeServiceRequestDateToIso(value);
  if (!iso) return false;

  if (minDate && iso < minDate) return false;
  if (maxDate && iso > maxDate) return false;

  return true;
};

export const getServiceRequestDateBoundsValidationMessage = (field, label, t) => {
  const translate =
    typeof t === "function"
      ? t
      : (key, options = {}) =>
          key.replace("{{label}}", options.label ?? label ?? "");

  if (hasServiceRequestDateBoundsConfigError(field)) {
    return translate("{{label}} has invalid date configuration", {
      label,
    });
  }

  const excludeToday =
    field?.excludeCurrentDate === commonConstants.YES_OPTION_TYPE;
  const pastOnly =
    field?.enablePastDateOnly === commonConstants.YES_OPTION_TYPE;
  const futureOnly =
    field?.isFutureDate === commonConstants.YES_OPTION_TYPE;

  if (pastOnly) {
    return excludeToday
      ? translate("{{label}} must be before today", { label })
      : translate("{{label}} must be today or a past date", { label });
  }

  if (futureOnly) {
    return excludeToday
      ? translate("{{label}} must be after today", { label })
      : translate("{{label}} must be today or a future date", { label });
  }

  return translate("{{label}} must be a valid date", { label });
};

export const isIsoDateOnOrBeforeToday = (value) => {
  if (!value) return true;

  let date = null;
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    date = new Date(value);
  } else if (typeof value === "object" && typeof value.format === "function") {
    const formatted = value.format("YYYY-MM-DD");
    date = formatted ? parseIsoDateOnly(formatted) : null;
  } else {
    date = parseIsoDateOnly(String(value));
  }

  if (!date) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  return date.getTime() <= today.getTime();
};

function resolveServiceRequestReceiptAssetValue(fieldMeta, item) {
  const listMultiRows =
    Array.isArray(item) &&
    item.length > 0 &&
    typeof item[0] === "object" &&
    item[0] != null
      ? item
      : Array.isArray(item?.fieldValue)
        ? item.fieldValue
        : null;

  const isTemplateOneSingleSelect =
    fieldMeta?.fieldType === serviceRequestConstants.ASSET_TYPE_FIELD_TYPE &&
    fieldMeta?.isMultiselect === commonConstants.NO_OPTION_TYPE &&
    fieldMeta?.assetsType?.template ===
      serviceRequestConstants.ASSET_TEMPLATE_1_TYPE;

  if (isTemplateOneSingleSelect) {
    const assetItem =
      Array.isArray(item) &&
      item.length > 0 &&
      typeof item[0] === "object" &&
      item[0] != null
        ? item[0]
        : item;
    const displayValue =
      getServiceRequestAssetHeading(assetItem) ||
      (assetItem?.fieldValue != null && assetItem?.fieldValue !== ""
        ? String(assetItem.fieldValue)
        : "");

    return displayValue || null;
  }

  if (listMultiRows?.length) {
    return listMultiRows
      .map((row) => getServiceRequestAssetHeading(row) || row?.fieldValue || "")
      .filter(Boolean)
      .join(", ");
  }

  if (
    fieldMeta?.fieldType === serviceRequestConstants.ASSET_TYPE_FIELD_TYPE &&
    (item?.heading ||
      item?.subHeading1 ||
      item?.subHeading2 ||
      item?.subHeading3)
  ) {
    return [
      item?.heading,
      item?.subHeading1,
      item?.subHeading2,
      item?.subHeading3,
    ]
      .filter((part) => part != null && part !== "")
      .map(String)
      .join(" ");
  }

  return getServiceRequestAssetHeading(item) || item?.fieldValue || "-";
}

function resolveServiceRequestReceiptDropdownValue(fieldMeta, item) {
  if (item?.dropdownValue) {
    return String(item.dropdownValue);
  }

  if (Array.isArray(fieldMeta?.customDropdownOptions)) {
    const matchedOption = fieldMeta.customDropdownOptions.find(
      (option) => option?.optionCode === item?.fieldValue,
    );
    if (matchedOption?.optionValue) {
      return String(matchedOption.optionValue);
    }
  }

  return item?.fieldValue != null ? String(item.fieldValue) : "-";
}

export function buildServiceRequestReceiptRows({
  initiateData = {},
  templateFieldsList = [],
  t = (key) => key,
}) {
  const data =
    typeof initiateData === "object" && !Array.isArray(initiateData)
      ? initiateData
      : {};

  const templateFieldMap = new Map(
    (templateFieldsList || [])
      .filter((field) => field?.fieldCode)
      .map((field) => [field.fieldCode, field]),
  );

  const sortedFieldCodes = Object.keys(data).sort((left, right) => {
    const leftSequence = Number(templateFieldMap.get(left)?.sequence ?? 0);
    const rightSequence = Number(templateFieldMap.get(right)?.sequence ?? 0);
    return leftSequence - rightSequence;
  });

  const rows = [];

  sortedFieldCodes.forEach((fieldCode) => {
    const fieldMeta = templateFieldMap.get(fieldCode);
    const item = data[fieldCode];

    if (!fieldMeta || item == null) {
      return;
    }

    if (fieldMeta?.showInFormOnly === commonConstants.YES_OPTION_TYPE) {
      return;
    }

    const label = fieldMeta?.title || fieldCode;

    if (
      fieldMeta?.fieldType === serviceRequestConstants.DATE_RANGE_FIELD_TYPE &&
      item?.fromValue &&
      item?.toValue
    ) {
      rows.push({
        label: `${label} (From)`,
        value: DateFormatter(item.fromValue),
      });
      rows.push({
        label: `${label} (To)`,
        value: DateFormatter(item.toValue),
      });
      return;
    }

    if (
      fieldMeta?.fieldType === serviceRequestConstants.VALUE_RANGE_FIELD_TYPE &&
      item?.startValue !== undefined &&
      item?.endValue !== undefined
    ) {
      rows.push({
        label: `${label} (From)`,
        value: formatServiceRequestSensitiveDisplayValue(
          item.startValue,
          fieldMeta,
        ),
      });
      rows.push({
        label: `${label} (To)`,
        value: formatServiceRequestSensitiveDisplayValue(
          item.endValue,
          fieldMeta,
        ),
      });
      return;
    }

    if (
      fieldMeta?.fieldType === serviceRequestConstants.FILE_UPLOAD_FIELD_TYPE
    ) {
      const fileItems = Array.isArray(item?.fileUploadItems)
        ? item.fileUploadItems
        : Array.isArray(item?.fileItems)
          ? item.fileItems
          : [];

      if (fileItems.length > 0) {
        fileItems
          .map((file) => String(file?.fileName ?? "").trim())
          .filter(Boolean)
          .forEach((name, idx) => {
            rows.push({
              label: idx === 0 ? label : "",
              value: name,
            });
          });
        return;
      }

      rows.push({ label, value: t("File attached") });
      return;
    }

    if (
      fieldMeta?.fieldType === serviceRequestConstants.AMOUNT_FIELD_TYPE &&
      item?.fieldValue != null &&
      item?.fieldValue !== ""
    ) {
      rows.push({ label, value: NumberFormatter(item.fieldValue) });
      return;
    }

    if (
      fieldMeta?.fieldType === serviceRequestConstants.NUMBER_FIELD_TYPE &&
      item?.fieldValue != null &&
      item?.fieldValue !== ""
    ) {
      rows.push({
        label,
        value: formatServiceRequestNumberDisplayValue(
          fieldMeta,
          item.fieldValue,
        ),
      });
      return;
    }

    if (
      fieldMeta?.fieldType === serviceRequestConstants.FORMULA_FIELD_TYPE &&
      item?.fieldValue != null &&
      item?.fieldValue !== ""
    ) {
      const formulaDisplay = isServiceRequestSensitiveField(fieldMeta)
        ? formatServiceRequestSensitiveDisplayValue(
            item.fieldValue,
            fieldMeta,
          )
        : NumberFormatter(item.fieldValue);

      rows.push({ label, value: formulaDisplay });
      return;
    }

    if (
      fieldMeta?.fieldType === serviceRequestConstants.DATE_FIELD_TYPE &&
      item?.fieldValue
    ) {
      rows.push({ label, value: DateFormatter(item.fieldValue) });
      return;
    }

    if (
      fieldMeta?.fieldType === serviceRequestConstants.DROPDOWN_FIELD_TYPE &&
      fieldMeta?.isMultiselect === commonConstants.YES_OPTION_TYPE &&
      Array.isArray(item)
    ) {
      const multiValue = item
        .map(
          (row) =>
            row?.dropdownValue ??
            row?.heading ??
            (row?.fieldValue != null ? String(row.fieldValue) : ""),
        )
        .filter(Boolean)
        .join(", ");

      if (multiValue) {
        rows.push({ label, value: multiValue });
      }
      return;
    }

    if (fieldMeta?.fieldType === serviceRequestConstants.DROPDOWN_FIELD_TYPE) {
      rows.push({
        label,
        value: resolveServiceRequestReceiptDropdownValue(fieldMeta, item),
      });
      return;
    }

    if (
      fieldMeta?.fieldType === serviceRequestConstants.ASSET_TYPE_FIELD_TYPE
    ) {
      const assetValue = resolveServiceRequestReceiptAssetValue(
        fieldMeta,
        item,
      );
      if (assetValue) {
        rows.push({ label, value: assetValue });
      }
      return;
    }

    const value =
      item?.heading != null && item?.heading !== ""
        ? String(item.heading)
        : item?.fieldValue != null && item?.fieldValue !== ""
          ? String(item.fieldValue)
          : "-";

    rows.push({
      label,
      value: formatServiceRequestSensitiveDisplayValue(value, fieldMeta, "-"),
    });
  });

  return rows;
}
